import React, { useCallback, useState, useEffect } from "react";
import { Calendar as _Calendar, Text } from "@bookingcom/bui-react";
import useCalendar from "./useCalendar.js";
import { isDateInRange, formatDateKey, getPriceVariant, getPriceCount } from "../../utils/fareCalendar.js";
import { useFareCalendar, useFareCalendarEnabled } from "../FareCalendar/useFareCalendar.js";
import useScrollBaseMonthChange from "../../hooks/useScrollBaseMonthChange.js";
import styles from "./Calendar.module.css";
const attributes = {
    key: "data-ui-name",
    value: "calendar_body"
};
export default function Calendar(props) {
    const { segmentIndex, onDateChange, onLoadingChange, onErrorChange, onMonthChange, ...restProps } = props;
    const { setDateValues, isRoundTrip, startDate, endDate, baseDate, fareCalendarConfig, ...calendarProps } = useCalendar({
        segmentIndex
    });
    const fareCalendarEnabled = useFareCalendarEnabled(segmentIndex, fareCalendarConfig, restProps.mode);
    const [_baseDate] = useState(baseDate);
    const { priceMap, handleMonthChange, loading, error } = useFareCalendar({
        baseDate: _baseDate,
        mode: restProps.mode,
        enabled: fareCalendarEnabled,
        segmentIndex
    });
    const handleScrollMonthChange = useCallback((date) => {
        handleMonthChange(date);
    }, [handleMonthChange]);
    useScrollBaseMonthChange({
        isActive: restProps.mode === "vertical" && fareCalendarEnabled,
        selector: attributes,
        monthsCallback: handleScrollMonthChange
    });
    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);
    useEffect(() => {
        onErrorChange?.(error);
    }, [error, onErrorChange]);
    const handleOnDateChange = useCallback((data) => {
        const { endDate, startDate } = data;
        setDateValues({ from: startDate || undefined, to: endDate || undefined });
        onDateChange({ from: startDate || undefined, to: endDate || undefined });
    }, [setDateValues, onDateChange]);
    const renderDay = useCallback((date) => {
        const yyyy = date.getFullYear();
        const m = date.getMonth() + 1;
        const d = date.getDate();
        const mm = `0${m}`.slice(-2);
        const dd = `0${d}`.slice(-2);
        return React.createElement("span", { "data-ui-name": `calendar_cell_${yyyy}-${mm}-${dd}` }, d);
    }, []);
    const renderAttachment = useCallback(({ date }) => {
        if (!isDateInRange(date, calendarProps.minDate, calendarProps.maxDate)) {
            return undefined;
        }
        if (loading) {
            return {
                loading: true
            };
        }
        const dateKey = formatDateKey(date);
        const priceIndication = priceMap[dateKey];
        if (!priceIndication) {
            return {
                text: React.createElement(Text, { color: "disabled" }, "-")
            };
        }
        const variant = getPriceVariant(priceIndication);
        const count = getPriceCount(priceIndication);
        if (variant === "neutral") {
            const dateTime = date.getTime();
            const isSelected = (startDate && dateTime === startDate.getTime()) ||
                (endDate && dateTime === endDate.getTime()) ||
                (startDate && endDate && dateTime > startDate.getTime() && dateTime < endDate.getTime());
            return {
                text: (React.createElement("div", { className: !isSelected ? styles.neutralAttachment : "" },
                    React.createElement(Text, { color: isSelected ? "white" : "neutral" }, "\u2022\u2022")))
            };
        }
        return {
            count,
            variant
        };
    }, [calendarProps.minDate, calendarProps.maxDate, priceMap, loading, startDate, endDate]);
    const onBaseMonthChange = useCallback((date) => {
        handleMonthChange(date);
        onMonthChange?.(date);
    }, [handleMonthChange, onMonthChange]);
    return (React.createElement(_Calendar, { ...calendarProps, ...restProps, onDateChange: handleOnDateChange, startDate: startDate, endDate: isRoundTrip ? endDate : null, baseDate: _baseDate, renderDay: renderDay, attributes: { [attributes.key]: attributes.value }, renderAttachment: fareCalendarEnabled ? renderAttachment : undefined, onBaseMonthChange: onBaseMonthChange }));
}
